import Vue from 'vue';
import Vuex from 'vuex';
import createOpportunity from './create-opportunity';
import store from '@/stores';
import EventBus from '@/scripts/event-bus';
import { activitiesTab } from './opportunity-activities-tab/opportunity-activities-tab-module';
import { filesTab } from './opportunity-files-tab/opportunity-files-tab-module';
import { registeredDealsTab } from './opportunity-registered-deals-tab/opportunity-registered-deals-tab-module';
import { detailsTab } from './opportunity-details-tab/opportunity-details-tab-module';
import { OPPORTUNITY_EDIT_CONTEXT } from './opportunity-helpers';

Vue.use(Vuex);

export function createOpportunityDetailsWidgetStore() {
    return new Vuex.Store({
        state: {
            widget: null,
            selectedNavItemIndex: 0,
            navItems: [
                {
                    key: 0,
                    label: 'Details'
                },
                {
                    key: 1,
                    label: 'Activities'
                },
                {
                    key: 2,
                    label: 'Registered Deals'
                },
                {
                    key: 3,
                    label: 'Files'
                }
            ],
            loading: false,
            editing: false,
            editingContext: null,
            saving: false,
            opportunity: createOpportunity(),
            customFields: [],
            loadingCustomFields: false,
            contactIds: [],
            submitted: false,
            hasUnsavedChanges: false,
            availableContacts: [],
            users: [],
            minimumGrossMargin: null,
            secondaryContactsQuery: null
        },

        getters: {
            navItems(state, getters) {
                if(!getters.isNewOpportunity) {
                    return [
                        ...state.navItems,
                        {
                            key: 4,
                            label: 'Summary',
                        }
                    ]
                }

                return state.navItems;
            },

            selectedNavItem(state, getters) {
                return getters.navItems[state.selectedNavItemIndex] || getters.navItems[0];
            },

            isNewOpportunity(state) {
                return state.opportunity.id == null;
            },

            isEditingOpportunity(state, getters) {
                return getters.isNewOpportunity || (!getters.isNewOpportunity && state.editing);
            },

            loading(state, getters) {
                return !getters.isNewOpportunity && state.loading;
            },

            clientId() {
                return store.state.context.client.id;
            },

            contactIds(state) {
                const contactIds = [];
                if(state.opportunity.contact) {
                    contactIds.push(state.opportunity.contact.id);
                }

                if(state.opportunity.secondaryContacts) {
                    const secondaryContactIds = state.opportunity.secondaryContacts.map(c => c.id);
                    contactIds.push(...secondaryContactIds);
                }

                return contactIds;
            },

            hasMetMinimumGrossMargin(state) {
                const { minimumGrossMargin } = state;
                if (!minimumGrossMargin) {
                    return true;
                }
                const { percentage } = state.opportunity.grossMargin;

                const isBelowMinimum = percentage < minimumGrossMargin;

                return !isBelowMinimum;
            }
        },

        mutations: {
            initialize(state, { opportunityId, widget, editing, editingContext, selectedNavItemIndex }) {
                state.opportunity = createOpportunity();
                state.opportunity.id = opportunityId;
                state.editing = editing || false;
                state.editingContext = editingContext || OPPORTUNITY_EDIT_CONTEXT.All;
                state.selectedNavItemIndex = selectedNavItemIndex || 0;
                state.widget = widget;
            },

            setSelectedNavItemIndex(state, selectedNavItemIndex) {
                state.selectedNavItemIndex = selectedNavItemIndex || 0;
            },

            updateOpportunity(state, updateFn) {
                const opportunity = updateFn(state.opportunity);
                if (opportunity) {
                    state.opportunity = {
                        ...state.opportunity,
                        ...opportunity
                    };
                }

                if (!state.hasUnsavedChanges) {
                    state.hasUnsavedChanges = true;
                }
            },

            setCustomFields(state, customFields) {
                state.customFields = customFields;
            },

            setLoadingCustomFields(state, loading) {
                state.loadingCustomFields = loading;
            },

            setLoading(state, loading) {
                state.loading = loading;
            },

            setAvailableContacts(state, contacts) {
                state.availableContacts = contacts;
            },

            setUsers(state, users) {
                state.users = users;
            },

            setSecondaryContactsQuery(state, value) {
                state.secondaryContactsQuery = value;
            },

            setMinimumGrossMargin(state, minimumGrossMargin) {
                state.minimumGrossMargin = minimumGrossMargin;
            },

            setSubmitted(state, submitted) {
                state.submitted = submitted;
            },

            setEditing(state, { editing, editingContext }) {
                state.editing = editing;

                if(editingContext) {
                    state.editingContext = editingContext;
                }

                if(state.editing && state.editingContext === OPPORTUNITY_EDIT_CONTEXT.Details) {
                    state.selectedNavItemIndex = 0;
                }
            },

            setSaving(state, saving) {
                state.saving = saving;
            }
        },


        actions: {
            closeWidget({ state }) {
                EventBus.$emit('close-widget', state.widget);
            },
        },
        modules: {
            detailsTab,
            activitiesTab,
            registeredDealsTab,
            filesTab,
        }
    });
}